package com.rajen.angrejidictionary.pages.admin

import androidx.compose.runtime.*
import com.rajen.angrejidictionary.analytics.Umami
import com.rajen.angrejidictionary.components.layouts.PageLayoutData
import com.rajen.angrejidictionary.data.local.AdminAuthManager
import com.rajen.angrejidictionary.data.remote.ApiConfig
import com.rajen.angrejidictionary.presentation.viewmodel.AdminDashboardViewModel
import com.rajen.angrejidictionary.presentation.viewmodel.NotificationsViewModel
import com.rajen.angrejidictionary.presentation.viewmodel.SuggestionsViewModel
import com.rajen.angrejidictionary.util.readClipboardText
import com.rajen.angrejidictionary.util.writeClipboardText
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private val Maroon = Color.rgb(0x21, 0x00, 0x00)
private val Muted = Color.rgb(0xB6, 0x9B, 0x8C)
private val Accent = Color.rgb(0xD3, 0x35, 0x3E)
private val PanelBg = Color.rgb(0xFF, 0xF7, 0xF1)
private val ChipBg = Color.rgb(0xF8, 0xDD, 0xC8)

val AdminDashboardCardStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(PanelBg)
        .borderRadius(28.px)
        .padding(1.5.cssRem)
        .boxShadow(offsetY = 10.px, blurRadius = 30.px, color = Color.rgba(0, 0, 0, 0.06f))
}

// Grid on mobile so tabs get equal, thumb-friendly touch targets instead of
// wrapping unevenly; reverts to an inline row on larger screens.
val AdminTabBarStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { repeat(4) { size(1.fr) } }
            .gap(0.5.cssRem)
    }
    Breakpoint.MD {
        Modifier
            .display(DisplayStyle.Flex)
            .width(auto)
            .gap(0.75.cssRem)
    }
}

val ActiveTabStyle = CssStyle.base {
    Modifier
        .backgroundColor(Accent)
        .color(Colors.White)
        .padding(0.7.cssRem, 1.cssRem)
        .borderRadius(12.px)
        .cursor(Cursor.Pointer)
        .fontWeight(FontWeight.Medium)
        .textAlign(TextAlign.Center)
        .fontSize(0.9.cssRem)
}

val InactiveTabStyle = CssStyle.base {
    Modifier
        .backgroundColor(ChipBg)
        .color(Maroon)
        .padding(0.7.cssRem, 1.cssRem)
        .borderRadius(12.px)
        .cursor(Cursor.Pointer)
        .fontWeight(FontWeight.Medium)
        .textAlign(TextAlign.Center)
        .fontSize(0.9.cssRem)
}

val SuccessStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(Color.rgb(0xE8, 0xF5, 0xE9))
        .borderRadius(8.px)
        .padding(0.75.cssRem)
        .color(Color.rgb(0x2E, 0x7D, 0x32))
        .fontSize(0.9.cssRem)
}

val PreviewCardStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(ChipBg)
        .borderRadius(16.px)
        .padding(1.25.cssRem)
}

val AdminWordCardStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(ChipBg)
        .borderRadius(12.px)
        .padding(1.cssRem)
}

// Wraps the word title in AdminWordRow so it opens the public word-detail
// page in a new tab — underlines on hover as a discoverable affordance.
@OptIn(ExperimentalComposeWebApi::class)
val AdminWordLinkStyle = CssStyle {
    base {
        Modifier
            .textDecorationLine(TextDecorationLine.None)
            .cursor(Cursor.Pointer)
    }
    hover {
        Modifier.textDecorationLine(TextDecorationLine.Underline)
    }
}

val WordOfDayPanelStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(Colors.White)
        .borderRadius(14.px)
        .padding(1.cssRem)
        .border(1.px, LineStyle.Solid, Color.rgba(0xD3, 0x35, 0x3E, 0.15f))
}

// Small secondary action (Clear / Paste) — distinct from the full-width red submit button.
val AdminOutlineButtonStyle = CssStyle.base {
    Modifier
        .padding(topBottom = 0.5.cssRem, leftRight = 0.9.cssRem)
        .backgroundColor(Colors.White)
        .color(Accent)
        .border(1.5.px, LineStyle.Solid, Color.rgba(0xD3, 0x35, 0x3E, 0.35f))
        .borderRadius(10.px)
        .fontWeight(FontWeight.Medium)
        .fontSize(0.85.cssRem)
        .cursor(Cursor.Pointer)
        .whiteSpace(WhiteSpace.NoWrap)
}

// Two-column on desktop (input | sticky live preview), single column on mobile.
val AddWordLayoutStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(1.5.cssRem)
    }
    Breakpoint.LG {
        Modifier
            .flexDirection(FlexDirection.Row)
            .alignItems(AlignItems.FlexStart)
    }
}

val AddWordInputColumnStyle = CssStyle {
    base { Modifier.fillMaxWidth() }
    Breakpoint.LG { Modifier.flex(1).minWidth(0.px) }
}

val AddWordPreviewColumnStyle = CssStyle {
    base { Modifier.fillMaxWidth() }
    Breakpoint.LG {
        Modifier
            .width(360.px)
            .flexShrink(0)
            .position(Position.Sticky)
            .top(1.cssRem)
    }
}

@InitRoute
fun initAdminDashboard(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Admin Dashboard",
            description = "Manage daily words for Angreji Dictionary."
        )
    )
}

@Page("/admin")
@Layout(".components.layouts.PageLayout")
@Composable
fun AdminDashboardPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val viewModel = remember { AdminDashboardViewModel() }
    val suggestionsViewModel = remember { SuggestionsViewModel() }
    val notificationsViewModel = remember { NotificationsViewModel() }
    var activeTab by remember { mutableStateOf(0) }

    LaunchedEffect(Unit) {
        if (!AdminAuthManager.isLoggedIn()) {
            ctx.router.navigateTo("/admin/login")
        }
    }

    LaunchedEffect(viewModel.forceLogout) {
        if (viewModel.forceLogout) {
            ctx.router.navigateTo("/admin/login")
        }
    }

    // These two viewmodels can also hit a 401 (expired token) independently
    // of the main dashboard viewmodel, so they need the same redirect.
    LaunchedEffect(suggestionsViewModel.forceLogout) {
        if (suggestionsViewModel.forceLogout) {
            ctx.router.navigateTo("/admin/login")
        }
    }

    LaunchedEffect(notificationsViewModel.forceLogout) {
        if (notificationsViewModel.forceLogout) {
            ctx.router.navigateTo("/admin/login")
        }
    }

    // Once an edit is successfully saved, hop back to the word list to see it.
    LaunchedEffect(viewModel.wordUpdateCompleted) {
        if (viewModel.wordUpdateCompleted) {
            activeTab = 1
            viewModel.consumeWordUpdateCompleted()
        }
    }

    // Approve clicked on a suggestion whose word isn't in the dictionary yet:
    // jump to Add Word and link the two together.
    LaunchedEffect(suggestionsViewModel.pendingWordAddSuggestion) {
        val pending = suggestionsViewModel.pendingWordAddSuggestion
        if (pending != null) {
            viewModel.startAddWordForApproval(pending.id, pending.suggestedWord)
            activeTab = 0
            Umami.trackAdminTabSelect("add-word")
            suggestionsViewModel.consumePendingWordAdd()
        }
    }

    // Once that linked word is actually saved, approve the suggestion it was
    // added for — this is what makes it "one click". Watches a dedicated
    // one-shot event (set only at the moment of that exact creation) rather
    // than lastSubmittedWord, which stays non-null indefinitely after any
    // unrelated create/update and would otherwise cause a stale re-trigger.
    LaunchedEffect(viewModel.approvalReadySuggestionId) {
        val suggestionId = viewModel.approvalReadySuggestionId
        if (suggestionId != null) {
            suggestionsViewModel.updateStatus(suggestionId, "approved")
            viewModel.consumeApprovalReady()
        }
    }

    Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
        Column(
            Modifier.fillMaxWidth().maxWidth(1400.px).padding(leftRight = 1.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Header
            Row(
                Modifier.fillMaxWidth()
                    .justifyContent(JustifyContent.SpaceBetween)
                    .flexWrap(FlexWrap.Wrap)
                    .gap(1.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    SpanText(
                        text = "ADMIN DASHBOARD",
                        modifier = Modifier
                            .fontSize(clamp(1.6.cssRem, 5.vw, 2.2.cssRem))
                            .fontWeight(FontWeight.Bold)
                            .color(Accent)
                    )
                    SpanText(
                        text = "Welcome, ${AdminAuthManager.getUsername() ?: "Admin"}",
                        modifier = Modifier
                            .fontSize(0.95.cssRem)
                            .color(Muted)
                            .margin(top = 0.2.cssRem)
                    )
                }

                Div(
                    Modifier
                        .padding(topBottom = 0.6.cssRem, leftRight = 1.2.cssRem)
                        .backgroundColor(Accent)
                        .color(Colors.White)
                        .borderRadius(10.px)
                        .fontWeight(FontWeight.Bold)
                        .fontSize(0.9.cssRem)
                        .cursor(Cursor.Pointer)
                        .toAttrs {
                            onClick {
                                viewModel.logout()
                                ctx.router.navigateTo("/admin/login")
                            }
                        }
                ) {
                    Text("Logout")
                }
            }

            // Tabs
            Div(
                AdminTabBarStyle.toModifier().margin(top = 1.5.cssRem).toAttrs()
            ) {
                val addStyle = if (activeTab == 0) ActiveTabStyle else InactiveTabStyle
                Div(addStyle.toAttrs { onClick { activeTab = 0; Umami.trackAdminTabSelect("add-word") } }) {
                    Text(if (viewModel.editingWordId != null) "✏️ Edit Word" else "➕ Add Word")
                }

                val viewStyle = if (activeTab == 1) ActiveTabStyle else InactiveTabStyle
                Div(viewStyle.toAttrs { onClick { activeTab = 1; Umami.trackAdminTabSelect("view-words") } }) {
                    Text("📋 Words (${viewModel.totalWords})")
                }

                val suggestionsStyle = if (activeTab == 2) ActiveTabStyle else InactiveTabStyle
                Div(suggestionsStyle.toAttrs { onClick { activeTab = 2; Umami.trackAdminTabSelect("suggestions") } }) {
                    Row(
                        Modifier.fillMaxWidth().gap(0.35.cssRem),
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText("💡 Suggestions")
                        if (suggestionsViewModel.pendingCount > 0) {
                            TabBadge(suggestionsViewModel.pendingCount, onActiveTab = activeTab == 2)
                        }
                    }
                }

                val notificationsStyle = if (activeTab == 3) ActiveTabStyle else InactiveTabStyle
                Div(
                    notificationsStyle.toAttrs {
                        onClick { activeTab = 3; Umami.trackAdminTabSelect("notifications") }
                    }
                ) {
                    Text("📢 Notifications")
                }
            }

            // Content
            Column(
                AdminDashboardCardStyle.toModifier().margin(top = 1.25.cssRem),
                verticalArrangement = Arrangement.spacedBy(1.5.cssRem),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                when (activeTab) {
                    0 -> AddWordTab(
                        viewModel = viewModel,
                        scope = scope,
                        onCancelEdit = {
                            viewModel.cancelEdit()
                            activeTab = 1
                        }
                    )
                    1 -> ViewWordsTab(
                        viewModel = viewModel,
                        onEditWord = { id ->
                            viewModel.startEdit(id)
                            activeTab = 0
                            Umami.trackAdminTabSelect("add-word")
                        }
                    )
                    2 -> SuggestionsTab(suggestionsViewModel)
                    3 -> NotificationsTab(notificationsViewModel)
                }
            }
        }
    }
}

/** Small pill badge shown on a tab button — e.g. the count of pending suggestions. */
@Composable
private fun TabBadge(count: Long, onActiveTab: Boolean) {
    val bg = if (onActiveTab) Colors.White else Accent
    val fg = if (onActiveTab) Accent else Colors.White
    Div(
        Modifier
            .padding(leftRight = 0.4.cssRem)
            .minWidth(17.px)
            .height(17.px)
            .backgroundColor(bg)
            .color(fg)
            .borderRadius(999.px)
            .fontSize(0.68.cssRem)
            .fontWeight(FontWeight.Bold)
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .toAttrs()
    ) {
        Text(if (count > 99) "99+" else count.toString())
    }
}

@Composable
private fun AddWordTab(
    viewModel: AdminDashboardViewModel,
    scope: kotlinx.coroutines.CoroutineScope,
    onCancelEdit: () -> Unit
) {
    var clipboardError by remember { mutableStateOf<String?>(null) }
    val isEditing = viewModel.editingWordId != null

    Div(AddWordLayoutStyle.toAttrs()) {

        // ── Left / top: paste + parse ─────────────────────────────
        Column(
            AddWordInputColumnStyle.toModifier(),
            verticalArrangement = Arrangement.spacedBy(0.85.cssRem)
        ) {
            viewModel.pendingApprovalWord?.let { word ->
                Row(
                    Modifier.fillMaxWidth()
                        .backgroundColor(Color.rgb(0xE3, 0xF2, 0xFD))
                        .borderRadius(10.px)
                        .padding(0.7.cssRem, 1.cssRem)
                        .gap(0.6.cssRem)
                        .flexWrap(FlexWrap.Wrap),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        "💡 Add \"$word\" here to auto-approve its suggestion once saved.",
                        Modifier.fontSize(0.85.cssRem).color(Color.rgb(0x0D, 0x47, 0xA1))
                    )
                    Div(
                        AdminOutlineButtonStyle.toAttrs { onClick { viewModel.cancelPendingApproval() } }
                    ) { Text("✕ Unlink") }
                }
            }

            Row(
                Modifier.fillMaxWidth().justifyContent(JustifyContent.SpaceBetween).flexWrap(FlexWrap.Wrap)
                    .gap(0.5.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    SpanText(
                        if (isEditing) "Edit Word JSON" else "Paste Grok JSON Output",
                        Modifier.fontSize(1.15.cssRem).fontWeight(FontWeight.Bold).color(Maroon)
                    )
                    SpanText(
                        if (isEditing)
                            "Update the fields below, then re-validate before saving your changes."
                        else
                            "Validate before saving to the database.",
                        Modifier.fontSize(0.85.cssRem).color(Muted).margin(top = 0.15.cssRem)
                    )
                }

                Row(Modifier.gap(0.5.cssRem)) {
                    if (isEditing) {
                        Div(
                            AdminOutlineButtonStyle.toAttrs {
                                onClick { onCancelEdit() }
                            }
                        ) { Text("✕ Cancel Edit") }
                    }

                    Div(
                        AdminOutlineButtonStyle.toAttrs {
                            onClick {
                                scope.launch {
                                    val text = readClipboardText()
                                    if (text != null) {
                                        viewModel.onJsonInputChange(text)
                                        clipboardError = null
                                    } else {
                                        clipboardError = "Couldn't read clipboard — paste manually instead."
                                    }
                                }
                            }
                        }
                    ) { Text("📋 Paste") }

                    Div(
                        AdminOutlineButtonStyle.toAttrs {
                            onClick { viewModel.onJsonInputChange("") }
                        }
                    ) { Text("✕ Clear") }
                }
            }

            clipboardError?.let { err ->
                SpanText(err, Modifier.fontSize(0.8.cssRem).color(Color.rgb(0xC6, 0x28, 0x28)))
            }

            if (viewModel.isLoadingWordDetail) {
                SpanText("⏳ Loading word details…", Modifier.fontSize(0.85.cssRem).color(Muted))
            }

            viewModel.wordDetailError?.let { err ->
                Div(AdminErrorStyle.toAttrs()) { Text("⚠️ $err") }
            }

            // JSON Text Area
            org.jetbrains.compose.web.dom.TextArea {
                value(viewModel.jsonInput)
                onInput { viewModel.onJsonInputChange(it.value) }
                style {
                    width(100.percent)
                    minHeight(280.px)
                    padding(1.cssRem)
                    borderRadius(12.px)
                    border { width(1.px); style(LineStyle.Solid); color(Color.rgb(0xDD, 0xCC, 0xBB)) }
                    fontSize(0.85.cssRem)
                    fontFamily("monospace")
                    property("resize", "vertical")
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    property("line-height", "1.5")
                }
                attr(
                    "placeholder", """Paste the Grok JSON output here...
{
  "word": { "english_word": "...", "category": [...], ... },
  "word_forms_inflections": [...],
  ...
}"""
                )
            }

            SpanText(
                "${viewModel.jsonInput.length} characters",
                Modifier.fontSize(0.75.cssRem).color(Muted)
            )

            viewModel.jsonError?.let { error ->
                Div(AdminErrorStyle.toAttrs()) { Text("⚠️ $error") }
            }

            Div(
                AdminButtonStyle.toAttrs { onClick { viewModel.parseJson() } }
            ) {
                Text("🔍 Parse & Validate JSON")
            }
        }

        // ── Right / bottom: live preview, sticky on desktop ──────
        Column(
            AddWordPreviewColumnStyle.toModifier(),
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            val request = viewModel.parsedRequest
            viewModel.submitSuccess?.let { message ->
                SubmitSuccessCard(message = message, word = viewModel.lastSubmittedWord)
            }
            viewModel.submitError?.let { error ->
                Div(AdminErrorStyle.toAttrs()) { Text("❌ $error") }
            }
            if (request == null) {
                if (viewModel.submitSuccess == null) {
                    Column(
                        PreviewCardStyle.toModifier(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(0.5.cssRem)
                    ) {
                        SpanText("📝", Modifier.fontSize(1.6.cssRem))
                        SpanText(
                            "Parsed word details will appear here",
                            Modifier.fontSize(0.9.cssRem).color(Muted).textAlign(TextAlign.Center)
                        )
                    }
                }
            } else {
                Column(
                    PreviewCardStyle.toModifier(),
                    verticalArrangement = Arrangement.spacedBy(0.9.cssRem)
                ) {
                    SpanText(
                        if (isEditing) "✏️ Ready to update" else "✅ Ready to submit",
                        Modifier.fontSize(1.05.cssRem).fontWeight(FontWeight.Bold)
                            .color(Color.rgb(0x2E, 0x7D, 0x32))
                    )

                    // Premium toggle. Hidden while the edit detail is still loading so the
                    // admin never sees (or changes) a placeholder value.
                    if (!viewModel.isLoadingWordDetail) {
                        PremiumToggle(viewModel.isPremium, viewModel::onPremiumChange)
                        if (isEditing && !viewModel.premiumKnown) {
                            SpanText(
                                "Couldn't load the current premium status. Saving leaves it unchanged unless you change this toggle.",
                                Modifier.fontSize(0.78.cssRem).color(Muted)
                            )
                        }
                    }

                    Div(
                        AdminButtonStyle.toAttrs {
                            onClick { scope.launch { viewModel.submitWord() } }
                        }
                    ) {
                        Text(
                            when {
                                viewModel.isSubmitting && isEditing -> "⏳ Updating..."
                                viewModel.isSubmitting -> "⏳ Saving..."
                                isEditing -> "💾 Update Word"
                                else -> "💾 Save Word to Database"
                            }
                        )
                    }

                    Column(
                        Modifier.fillMaxWidth()
                            .backgroundColor(Colors.White)
                            .borderRadius(12.px)
                            .padding(0.9.cssRem, 1.cssRem)
                    ) {
                        PreviewField("Word", request.word.englishWord)
                        PreviewField("Part of Speech", request.word.partOfSpeech)
                        if (request.word.category.isNotEmpty()) {
                            PreviewField("Category", request.word.category.joinToString(", "))
                        }
                        PreviewField("Pronunciation", request.word.pronunciationGujarati)
                        PreviewField("Meanings", request.word.meaningGujarati.joinToString(", "))
                        PreviewField("Word Forms", request.wordForms.size.toString())
                        PreviewField("Similar Words", request.similarWords.size.toString())
                        PreviewField("Opposite Words", request.oppositeWords.size.toString())
                        if (request.phrasalUsage?.hasPhrasalForm == true) {
                            PreviewField("Phrasals", request.phrasalUsage.phrasals.size.toString(), isLast = true)
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun PremiumToggle(checked: Boolean, onChange: (Boolean) -> Unit) {
    org.jetbrains.compose.web.dom.Label(attrs = {
        style {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "0.6rem")
            property("cursor", "pointer")
            property("font-size", "0.9rem")
            property("color", "#210000")
        }
    }) {
        org.jetbrains.compose.web.dom.CheckboxInput(checked = checked) {
            onInput { onChange(it.value) }
            style {
                width(18.px)
                height(18.px)
                property("accent-color", "#D3353E")
                property("cursor", "pointer")
            }
        }
        Text("⭐ Premium word")
    }
}

@Composable
private fun AdminPremiumBadge() {
    Div(
        Modifier.padding(0.15.cssRem, 0.6.cssRem)
            .backgroundColor(Color.rgb(0xFF, 0xC1, 0x07))
            .color(Maroon)
            .borderRadius(999.px)
            .fontSize(0.7.cssRem)
            .fontWeight(FontWeight.Bold)
            .toAttrs()
    ) { Text("⭐ PREMIUM") }
}

@Composable
private fun SubmitSuccessCard(message: String, word: String?) {
    var copied by remember { mutableStateOf(false) }
    val localScope = rememberCoroutineScope()

    Column(
        Modifier.fillMaxWidth()
            .backgroundColor(Color.rgb(0xE8, 0xF5, 0xE9))
            .borderRadius(12.px)
            .padding(1.cssRem),
        verticalArrangement = Arrangement.spacedBy(0.6.cssRem)
    ) {
        SpanText(
            message,
            Modifier.fontSize(0.9.cssRem).fontWeight(FontWeight.Medium)
                .color(Color.rgb(0x2E, 0x7D, 0x32))
        )

        if (word != null) {
            val url = remember(word) { ApiConfig.wordPageUrl(word) }

            org.jetbrains.compose.web.dom.A(
                href = url,
                attrs = {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                    style {
                        fontSize(0.82.cssRem)
                        color(Color.rgb(0x19, 0x76, 0xD2))
                        property("word-break", "break-all")
                        property("text-decoration", "underline")
                    }
                }
            ) {
                Text(url)
            }

            Div(
                AdminOutlineButtonStyle.toAttrs {
                    onClick {
                        localScope.launch {
                            if (writeClipboardText(url)) {
                                copied = true
                            }
                        }
                    }
                }
            ) {
                Text(if (copied) "✓ Copied" else "🔗 Copy Link")
            }
        }
    }
}

@Composable
private fun PreviewField(label: String, value: String, isLast: Boolean = false) {
    Row(
        Modifier
            .fillMaxWidth()
            .gap(0.6.cssRem)
            .padding(topBottom = 0.5.cssRem)
            .thenIf(!isLast) {
                Modifier.borderBottom(1.px, LineStyle.Solid, Color.rgba(0x21, 0x00, 0x00, 0.08f))
            },
        verticalAlignment = Alignment.Top
    ) {
        SpanText(
            label.uppercase(),
            Modifier
                .fontSize(0.65.cssRem)
                .fontWeight(FontWeight.Bold)
                .letterSpacing(0.4.px)
                .color(Muted)
                .width(108.px)
                .flexShrink(0)
        )
        SpanText(
            value,
            Modifier.fontSize(0.9.cssRem).color(Maroon).overflowWrap(OverflowWrap.BreakWord)
        )
    }
}

// Small helper so a conditional modifier reads cleanly above.
private fun Modifier.thenIf(condition: Boolean, block: () -> Modifier): Modifier =
    if (condition) this.then(block()) else this

@Composable
private fun ViewWordsTab(
    viewModel: AdminDashboardViewModel,
    onEditWord: (String) -> Unit
) {
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(1.cssRem)) {
        SpanText(
            "All Words in Database",
            Modifier.fontSize(1.2.cssRem).fontWeight(FontWeight.Bold).color(Maroon)
        )

        // ── Word of the Day ───────────────────────────────────────
        Column(WordOfDayPanelStyle.toModifier(), verticalArrangement = Arrangement.spacedBy(0.4.cssRem)) {
            SpanText(
                "📌 WORD OF THE DAY",
                Modifier.fontSize(0.7.cssRem).fontWeight(FontWeight.Bold).letterSpacing(0.5.px).color(Muted)
            )
            when {
                viewModel.isLoadingWordOfDay -> SpanText(
                    "Loading…", Modifier.fontSize(0.9.cssRem).color(Muted)
                )
                viewModel.wordOfDayError != null -> SpanText(
                    "❌ ${viewModel.wordOfDayError}",
                    Modifier.fontSize(0.85.cssRem).color(Color.rgb(0xC6, 0x28, 0x28))
                )
                viewModel.pinnedWordOfDayId != null -> {
                    Row(
                        Modifier.fillMaxWidth().justifyContent(JustifyContent.SpaceBetween)
                            .flexWrap(FlexWrap.Wrap).gap(0.5.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            viewModel.pinnedWordOfDayLabel?.let { "Pinned: \"$it\"" }
                                ?: "Pinned (id: ${viewModel.pinnedWordOfDayId})",
                            Modifier.fontSize(1.cssRem).fontWeight(FontWeight.Bold).color(Accent)
                        )
                        Div(
                            AdminOutlineButtonStyle.toAttrs { onClick { viewModel.clearWordOfDayPin() } }
                        ) {
                            Text(if (viewModel.pinningWordId != null) "Clearing…" else "✕ Clear Override")
                        }
                    }
                }
                else -> SpanText(
                    "No word pinned — using the default daily rotation.",
                    Modifier.fontSize(0.9.cssRem).color(Muted)
                )
            }
        }

        // ── Search ──────────────────────────────────────────────
        Row(
            Modifier.fillMaxWidth().gap(0.5.cssRem).flexWrap(FlexWrap.Wrap),
            verticalAlignment = Alignment.CenterVertically
        ) {
            org.jetbrains.compose.web.dom.TextInput(
                value = viewModel.wordSearchQuery,
                attrs = {
                    onInput { viewModel.onWordSearchQueryChange(it.value) }
                    onKeyDown { event -> if (event.key == "Enter") viewModel.searchWords() }
                    attr("placeholder", "Search words by spelling…")
                    attr("aria-label", "Search words")
                    style {
                        property("flex", "1")
                        property("min-width", "160px")
                        padding(0.65.cssRem, 0.85.cssRem)
                        borderRadius(10.px)
                        border(1.px, LineStyle.Solid, rgb(0xDD, 0xCC, 0xBB))
                        fontSize(0.95.cssRem)
                        color(rgb(0x21, 0x00, 0x00))
                        backgroundColor(rgb(0xFF, 0xFF, 0xFF))
                        property("box-sizing", "border-box")
                        property("outline", "none")
                    }
                }
            )

            Div(
                Modifier.padding(0.65.cssRem, 1.1.cssRem)
                    .backgroundColor(Accent)
                    .color(Colors.White)
                    .borderRadius(10.px)
                    .fontSize(0.9.cssRem)
                    .fontWeight(FontWeight.Medium)
                    .cursor(Cursor.Pointer)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .toAttrs { onClick { viewModel.searchWords() } }
            ) { Text(if (viewModel.isSearchingWords) "Searching…" else "🔍 Search") }

            if (viewModel.wordSearchQuery.isNotBlank() || viewModel.wordSearchResults != null) {
                Div(
                    AdminOutlineButtonStyle.toAttrs { onClick { viewModel.clearWordSearch() } }
                ) { Text("✕ Clear") }
            }
        }

        viewModel.wordsActionMessage?.let { msg ->
            Div(
                Modifier.fillMaxWidth()
                    .backgroundColor(Color.rgb(0xE8, 0xF5, 0xE9))
                    .borderRadius(8.px)
                    .padding(0.75.cssRem)
                    .color(Color.rgb(0x2E, 0x7D, 0x32))
                    .toAttrs()
            ) {
                Text(msg)
            }
            LaunchedEffect(msg) {
                kotlinx.coroutines.delay(3000)
                viewModel.clearWordsActionMessage()
            }
        }

        val searchResults = viewModel.wordSearchResults

        if (searchResults != null) {
            // ── Search results ──────────────────────────────────
            if (viewModel.isSearchingWords) {
                SpanText("Searching...", Modifier.color(Muted).fontSize(0.95.cssRem))
            } else if (searchResults.isEmpty()) {
                SpanText(
                    viewModel.wordSearchError
                        ?: "No words matched \"${viewModel.wordSearchQuery.trim()}\".",
                    Modifier.color(Muted).fontSize(0.95.cssRem)
                )
            } else {
                SpanText(
                    "${searchResults.size} result${if (searchResults.size == 1) "" else "s"} for \"${viewModel.wordSearchQuery.trim()}\"",
                    Modifier.fontSize(0.85.cssRem).color(Muted)
                )
                Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                    searchResults.forEach { item ->
                        AdminWordRow(
                            id = item.id,
                            englishWord = item.word.englishWord,
                            partOfSpeech = item.word.partOfSpeech,
                            meaningGujarati = item.word.meaningGujarati,
                            category = item.word.category,
                            // Comes from the public search endpoint (nullable on the DTO).
                            isPremium = item.isPremium == true,
                            viewModel = viewModel,
                            onEditWord = onEditWord
                        )
                    }
                }
            }
        } else if (viewModel.isLoadingWords) {
            SpanText("Loading words...", Modifier.color(Muted).fontSize(0.95.cssRem))
        } else if (viewModel.wordsError != null) {
            Div(AdminErrorStyle.toAttrs()) { Text("❌ ${viewModel.wordsError}") }
        } else if (viewModel.words.isEmpty()) {
            SpanText(
                "No words found. Add your first word using the 'Add Word' tab.",
                Modifier.color(Muted).fontSize(0.95.cssRem)
            )
        } else {
            // ── Paginated list ───────────────────────────────────
            SpanText(
                "Showing ${viewModel.words.size} of ${viewModel.totalWords} words",
                Modifier.fontSize(0.85.cssRem).color(Muted)
            )

            Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                viewModel.words.forEach { word ->
                    AdminWordRow(
                        id = word.id,
                        englishWord = word.englishWord,
                        partOfSpeech = word.partOfSpeech,
                        meaningGujarati = word.meaningGujarati,
                        category = word.category,
                        isPremium = word.isPremium,
                        viewModel = viewModel,
                        onEditWord = onEditWord
                    )
                }
            }

            val totalPages = ((viewModel.totalWords + 19) / 20).toInt()
            if (totalPages > 1) {
                Row(
                    Modifier.fillMaxWidth().justifyContent(JustifyContent.Center)
                        .gap(0.75.cssRem).margin(top = 1.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (viewModel.currentPage > 1) {
                        Div(AdminButtonStyle.toAttrs { onClick { viewModel.loadWords(viewModel.currentPage - 1) } }) {
                            Text("← Previous")
                        }
                    }
                    SpanText(
                        "Page ${viewModel.currentPage} of $totalPages",
                        Modifier.padding(leftRight = 1.cssRem).fontWeight(FontWeight.Medium)
                    )
                    if (viewModel.currentPage < totalPages) {
                        Div(AdminButtonStyle.toAttrs { onClick { viewModel.loadWords(viewModel.currentPage + 1) } }) {
                            Text("Next →")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AdminWordRow(
    id: String,
    englishWord: String,
    partOfSpeech: String,
    meaningGujarati: List<String>,
    category: List<String>,
    isPremium: Boolean,
    viewModel: AdminDashboardViewModel,
    onEditWord: (String) -> Unit
) {
    Column(AdminWordCardStyle.toModifier()) {
        Row(
            Modifier.gap(0.5.cssRem).flexWrap(FlexWrap.Wrap),
            verticalAlignment = Alignment.CenterVertically
        ) {
            A(
                href = ApiConfig.wordPageUrl(englishWord),
                attrs = AdminWordLinkStyle.toAttrs {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                    onClick { Umami.trackAdminWordPreviewClick(id) }
                }
            ) {
                SpanText(
                    "$englishWord ↗",
                    Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(Accent)
                )
            }
            if (isPremium) AdminPremiumBadge()
        }
        SpanText(
            "$partOfSpeech • ${meaningGujarati.joinToString(", ")}",
            Modifier.fontSize(0.9.cssRem).color(Maroon)
                .margin(top = 0.25.cssRem).overflowWrap(OverflowWrap.BreakWord)
        )

        if (category.isNotEmpty()) {
            Row(Modifier.gap(0.35.cssRem).flexWrap(FlexWrap.Wrap).margin(top = 0.4.cssRem)) {
                category.forEach { cat ->
                    Div(
                        Modifier.padding(0.15.cssRem, 0.55.cssRem)
                            .backgroundColor(Colors.White)
                            .color(Muted)
                            .borderRadius(999.px)
                            .fontSize(0.72.cssRem)
                            .border(1.px, LineStyle.Solid, Color.rgba(0x21, 0x00, 0x00, 0.1f))
                            .toAttrs()
                    ) { Text(cat) }
                }
            }
        }

        val isDeleting = viewModel.deletingWordId == id
        val isPendingDelete = viewModel.pendingDeleteWordId == id
        val isPinned = viewModel.pinnedWordOfDayId == id
        val isPinning = viewModel.pinningWordId == id

        Row(
            Modifier.fillMaxWidth()
                .margin(top = 0.75.cssRem)
                .justifyContent(JustifyContent.FlexEnd)
                .gap(0.5.cssRem)
                .flexWrap(FlexWrap.Wrap),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (!isPendingDelete) {
                Div(
                    Modifier.padding(0.5.cssRem, 1.cssRem)
                        .backgroundColor(Color.rgb(0x19, 0x76, 0xD2))
                        .color(Colors.White)
                        .borderRadius(8.px)
                        .fontSize(0.85.cssRem)
                        .cursor(Cursor.Pointer)
                        .toAttrs { onClick { onEditWord(id) } }
                ) { Text("✏️ Edit") }

                Div(
                    Modifier.padding(0.5.cssRem, 1.cssRem)
                        .backgroundColor(if (isPinned) Color.rgb(0x2E, 0x7D, 0x32) else Color.rgb(0xFF, 0xC1, 0x07))
                        .color(if (isPinned) Colors.White else Color.rgb(0x21, 0x00, 0x00))
                        .borderRadius(8.px)
                        .fontSize(0.85.cssRem)
                        .cursor(Cursor.Pointer)
                        .toAttrs {
                            onClick {
                                if (!isPinning) {
                                    if (isPinned) viewModel.clearWordOfDayPin()
                                    else viewModel.pinWordOfDay(id, englishWord)
                                }
                            }
                        }
                ) {
                    Text(
                        when {
                            isPinning -> "⏳ …"
                            isPinned -> "📌 Pinned"
                            else -> "📌 Pin"
                        }
                    )
                }

                Div(
                    Modifier.padding(0.5.cssRem, 1.cssRem)
                        .backgroundColor(Color.rgb(0xC6, 0x28, 0x28))
                        .color(Colors.White)
                        .borderRadius(8.px)
                        .fontSize(0.85.cssRem)
                        .cursor(Cursor.Pointer)
                        .toAttrs { onClick { viewModel.requestDeleteWord(id) } }
                ) { Text("🗑 Delete") }
            } else {
                SpanText(
                    "Delete this word?",
                    Modifier.fontSize(0.85.cssRem).color(Color.rgb(0xC6, 0x28, 0x28))
                )

                Div(
                    Modifier.padding(0.5.cssRem, 1.cssRem)
                        .backgroundColor(Color.rgb(0x8E, 0x1A, 0x1A))
                        .color(Colors.White)
                        .borderRadius(8.px)
                        .fontSize(0.85.cssRem)
                        .cursor(Cursor.Pointer)
                        .toAttrs { onClick { if (!isDeleting) viewModel.deleteWord(id) } }
                ) { Text(if (isDeleting) "Deleting…" else "⚠️ Confirm") }

                Div(
                    Modifier.padding(0.5.cssRem, 1.cssRem)
                        .backgroundColor(Color.rgb(0x75, 0x75, 0x75))
                        .color(Colors.White)
                        .borderRadius(8.px)
                        .fontSize(0.85.cssRem)
                        .cursor(Cursor.Pointer)
                        .toAttrs { onClick { viewModel.cancelDeleteWord() } }
                ) { Text("Cancel") }
            }
        }
    }
}